/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Smarts Conversation UI
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */


/**
 * This is a temporary class that is used to generate dashboard spec and will be replaced when Dashboard's API for generating
 * spec is ready.
 * @param id
 * @param dashboardSpecVersion
 * @returns {{build: (function(*, *=): {id: *, boardSpec: {name: string, theme: string, version: number, layout: {id: string, items: Array, style: {height: string}, type: string, pageSize: {width: number, height: number}, layoutPositioning: string}, dataSources: {version: string, sources: *[]}, widgets, eventGroups: Array}, isAuthoringMode: boolean}), buildTabContents: (function(*, *=): *)}}
 */
function dashboardSpecBuilder(id, dashboardSpecVersion) {
	const tabItems = [] //contains containers list

	let layout = {
		id: `${id}_layout`,
		items: tabItems,
		style: {
			height: '100%'
		},
		type: 'tab',
		pageSize: {
			width: 1280,
			height: 720
		},
		layoutPositioning: 'relative'
	}

	let dataSources = {
		version: '1.0',
		sources: [{
			shaping: {
				'embeddedModuleUpToDate': true
			}
		}]
	}

	let widgets = {}
	let eventGroups = []

	let boardSpec = {
		name: 'New dashboard',
		theme: 'defaultTheme',
		version: dashboardSpecVersion,
		layout: layout,
		dataSources: dataSources,
		widgets: widgets,
		eventGroups: eventGroups
	}

	let dashboardSpec = {
		id: id,
		boardSpec: boardSpec,
		isAuthoringMode: true
	}

	function createWidgetGenericPageItem( widgetId, style, label){
		return {
			id: widgetId,
			style: style,
			type: 'widget',
			name: label
		}
	}

	function addEventGroup(tabId, widgetIds){
		const eventGroupItem = {
			id: `${tabId}:1`,
			widgetIds
		}
		eventGroups.push( eventGroupItem )
	}


	function setSourceInfo({ modelRefId:id, sourceType:type, sourceId:assetId }){
		Object.assign(dataSources.sources[0], { assetId, type,  id })
	}

	function createTabWithContent(label, tabIndex, chartInfoList){
		let genericPageItems = [{
			id: `${id}_templateIndicator_${tabIndex}`,
			style: {
				top: '0%',
				left: '0%',
				right: '0%',
				bottom: '0%'
			},
			type: 'templateIndicator'
		}]

		let genericPage =  {
			id: `${id}_genericPage_${tabIndex}`,
			css: 'templateBox aspectRatio_default',
			items: genericPageItems,
			type: 'genericPage',
			layoutPositioning: 'relative'
		}

		const containerSpec =  {
			id: `${id}_container_${tabIndex}`,
			items: [ genericPage ],
			type: 'container',
			title: {
				translationTable: {
					Default: label || `Tab ${tabIndex}`
				}
			},
			templateName: 'Template9'
		}

		// Add livewidgets - both the layout and the spec
		let widgetIds = []
		chartInfoList.forEach( (chartInfo, index) => {
			const { chart, spec, style } = chartInfo
			const widgetId = `widget_${tabIndex}${index}` + Date.now()
			spec.id = widgetId
			genericPageItems.push(createWidgetGenericPageItem(widgetId, style, chart.label))
			widgets[widgetId] = spec
			widgetIds.push(widgetId)
		})

		return { containerSpec : containerSpec, widgetIds }

	}

	/**
	 * Add eventgroup and widgets to tab
	 * @param label
	 * @param tabIndex
	 * @param chartInfoList
	 */
	function addTab( label, tabIndex, chartInfoList ){
		const tab = createTabWithContent( label, tabIndex, chartInfoList)
		tabItems.push( tab.containerSpec )
		addEventGroup(tab.containerSpec.id, tab.widgetIds)
	}

	/**
	 * Build tab contents to be consumed by DashboardAPI.CanvasAPI.addContent()
	 * @param contents
	 * @param sourceInfo
	 * @returns {*}
	 */
	function buildTabContents( contents, sourceInfo ) {
		return contents.map(({ chartInfoList, tabLabel }, tabIndex) => {
			widgets = {}
			const tab = createTabWithContent(tabLabel, tabIndex, chartInfoList)
			setSourceInfo(sourceInfo)
			return { widgets, layout : tab.containerSpec, dataSources }
		})
	}

	function build( contents, sourceInfo ){
		contents.forEach( ({ tabLabel, chartInfoList }, index ) => {
			addTab(tabLabel, index, chartInfoList)
		})

		setSourceInfo(sourceInfo)
		return dashboardSpec
	}

	return {
		build,
		buildTabContents
	}
}

const NewDashboardSpec = {
	isAuthoringMode: true,
	boardSpec: {
		name: 'New dashboard',
		layout: {
			id: 'page0_' + Date.now(),
			items: [ {
				id: `container_1`,
				items: [],
				type: 'container',
				title: {
					translationTable: {
						Default: 'Tab 1'
					}
				},
				templateName: 'Template9'
			}],
			style: {
				height: '100%'
			},
			type: 'tab',
			showGrid: true,
			snapGrid: true,
			snapObjects: true,
			pageSize: {
				width: 1280,
				height: 720
			},
			layoutPositioning: 'relative'
		},
		pageContext: [],
		dataSources: {
			version: '1.0',
			sources: []
		},
		widgets: {}
	}
}

const SMARTS_TO_DASHBOARD_AGGREGATION_TYPE_MAP = {
	SUM: 'sum',
	COUNT_DISTINCT: 'countdistinct',
	COUNT: 'count',
	AVERAGE: 'avg',
	NONE: 'none',
	MINIMUM: 'min',
	MAXIMUM: 'max'
}

export default {
	build(id, contents, sourceInfo, dashboardSpecVersion) {
		return dashboardSpecBuilder(id, dashboardSpecVersion).build(contents, sourceInfo)
	},


	buildTabContents(id, contents, sourceInfo) {
		return dashboardSpecBuilder(id).buildTabContents(contents, sourceInfo)
	},

	getNewDashboardSpec(){
		NewDashboardSpec.id ='dashboard_' + Date.now()
		return NewDashboardSpec
	},

	/**
	 * Add id to widgets, remove model, add modelRef and (if available) autoBinning
	 * @param id
	 * @param widgetId
	 * @param chartRecommendation
	 * @param spec
	 * @returns updated spec
	 * @private
	 */
	fixupLiveWidgetSpec(spec, modelRefId, chartRecommendation) {
		spec = this._addProperties(spec)

		const aggregations = {}
		const topBottomFilterMap = {}
		const { filters, autoGroupingMap, columns } = chartRecommendation

		const FILTER_TYPES = {
			INCLUSION_EXCLUSION: 'INCLUSION_EXCLUSION',
			TOP_BOTTOM: 'TOP_BOTTOM',
			NUMERIC: 'NUMERIC'
		}

		if (columns) {
			columns.forEach(column => {
				if (column.aggregation) {
					column.id.forEach(id => aggregations[id] = column.aggregation)
				}
			})
		}

		if (filters) {
			filters.forEach(filter => {
				switch(filter.type) {
					case FILTER_TYPES.INCLUSION_EXCLUSION:
						spec.localFilters.push( this._addInclusionExclusionFilter(filter) )
						break
					case FILTER_TYPES.TOP_BOTTOM:
						topBottomFilterMap[filter.columnId] = filter
						break
					case FILTER_TYPES.NUMERIC:
						spec.localFilters.push( this._addNumericFilter(filter) )
						break
					default:
						// Filter type is not recognized - Do nothing.
						break
				}

			})
		}

		if (spec.data) {
			const dataViews = spec.data.dataViews

			dataViews.forEach(dataView => {
				const dataItems = dataView.dataItems

				//Fix up model info
				dataView.modelRef = modelRefId
				delete dataView.model

				dataItems.forEach(dataItem => {
					const { itemId, id, itemLabel } = dataItem

					if (aggregations[itemId]) {
						dataItem = this._addAggregateSpec(dataItem, aggregations[itemId])
					}

					if (autoGroupingMap && autoGroupingMap[itemId] && autoGroupingMap[itemId].groupSize) {
						dataItem = this._addAutoBinningSpec(dataItem, id, itemLabel, autoGroupingMap[itemId].groupSize)
					}

					if (topBottomFilterMap && topBottomFilterMap[itemId]) {
						dataItem = this._addTopBottomSpec(dataItem, topBottomFilterMap[itemId])
					}
				})

			})
		}

		return spec
	},

	_addAggregateSpec(dataItem, aggregation) {
		dataItem.aggregate = SMARTS_TO_DASHBOARD_AGGREGATION_TYPE_MAP[aggregation]

		return dataItem
	},

	_addAutoBinningSpec(dataItem, id, label, groupSize) {
		dataItem.binningDefinition = {
			id: id,
			binning: {
				auto: {
					numberOfBins: groupSize,
					maxDecPlaces: 2
				}
			},
			label: `${label} groups`
		}

		return dataItem
	},

	_addNumericFilter(filter) {
		const OPERATOR_TYPES = {
			'GREATER_THAN': 'gt',
			'LESS_THAN': 'lt'
		}

		const localFilter = {
			id: filter.columnId,
			columnId: filter.columnId,
			values: [
				{
					d: filter.value, // Display value
					u: filter.value // Unique value
				}
			],
			excludedValues: [],
			operator: OPERATOR_TYPES[filter.operator],
			preOrPost: 'pre'
		}

		return localFilter
	},

	_addInclusionExclusionFilter(filter) {
		let { inclusionValues, exclusionValues, label, filterCondition } = filter
		let isInclude = inclusionValues && !!inclusionValues.length

		if (filterCondition) {
			// Inclusion and Exclusion values are currently empty when
			// a filter condition is applied so we handle it differently.
			return this._addInclusionExclusionFilterWithCondition(filter)
		}

		if (filter.invertFlag) {
			isInclude = !isInclude;

			if (isInclude) {
				inclusionValues = exclusionValues;
			} else {
				exclusionValues = inclusionValues;
			}
		}

		const values = isInclude ? inclusionValues : exclusionValues

		const localFilter = {
			id: filter.columnId,
			columnId: filter.columnId,
			values: values.map( ({ displayValue:d, uniqueValue:u }) => ({ d, u })),
			operator: ( isInclude ) ? 'in' : 'notin',
			binsLabel : label
		}

		if (isInclude) {
			localFilter.excludedValues = []
		}

		return localFilter
	},

	_addInclusionExclusionFilterWithCondition({ columnId, filterCondition, invertFlag }) {
		const CONDITION_TYPES = {
			CONTAINS: 'containsignorecase',
			// Note: These are not currently supported by the Conversation Service
			// but may be added later.
			EQUALS: 'in',
			BEGINS_WITH: 'beginswith',
			ENDS_WITH: 'endswith'
		}

		let condition = {
			operator: CONDITION_TYPES[filterCondition.condition],
			itemId: columnId,
			ignoreCase: true,
			values: filterCondition.values,
			valueType: 'display'
		}

		if (filterCondition.values && filterCondition.values.length > 1) {
			const conditionWithOperator = { or: [] }
			filterCondition.values.forEach(value => {
				const tmpCondition = Object.assign({}, condition)
				tmpCondition.values = [value]
				conditionWithOperator.or.push(tmpCondition)
			})

			condition = conditionWithOperator
		}

		const localFilter = {
			id: columnId,
			columnId: columnId,
			values: [],
			excludedValues: [],
			operator: 'in',
			conditions: invertFlag ? { not: condition } : condition
		}

		return localFilter
	},

	_addTopBottomSpec(dataItem, topBottomFilter) {
		let { topBottomType, method } = topBottomFilter
		topBottomType = topBottomType.toLowerCase()

		if( !topBottomType ){
			return dataItem
		}

		method = method.toLowerCase()

		let newType = topBottomType + (method === 'count' ? 'count' : 'percent')
		let topBottomSpec = {
			operation: 'keep',
			topBottom: {
				type: newType,
				value: topBottomFilter.domainSize
			}
		}

		if (topBottomFilter.byColumn) {
			let context = { itemId : topBottomFilter.byColumn }
			const aggregationType = topBottomFilter.byAggregate
			if( aggregationType ){
				context.aggregate = SMARTS_TO_DASHBOARD_AGGREGATION_TYPE_MAP[aggregationType]
			}
			topBottomSpec.context = [context]
		}

		dataItem.selection = [topBottomSpec]
		return dataItem
	},

	_addProperties(spec) {
		spec.showTitle = !(spec.visId === 'summary')
		spec.borderColor = 'color2'
		spec.type = spec.type ? spec.type : 'live'
		spec.localFilters = []

		spec.properties = [{
			id: 'widget.legend.position',
			value: 'bottom'
		}]

		if( spec.visId === 'com.ibm.vis.rave2marimekko'){
			spec.properties.push({
				id: 'stacked.percent',
				value: false
			})
		}

		return spec
	}
}
